import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml, SafeUrl } from '@angular/platform-browser';
import { SignatureService } from '../../signature.service';

interface PrintGroup { name: string; tests: { name: string; code: string }[]; }

@Component({
  standalone: false,
  selector: 'app-blood-test-referral-print',
  templateUrl: './blood-test-referral-print.component.html',
  styleUrls: ['./blood-test-referral-print.component.scss']
})
export class BloodTestReferralPrintComponent implements OnInit {

  referral: any = null;
  groups: PrintGroup[] = [];
  lang = 'he';
  dir = 'rtl';
  loaded = false;
  printDate = '';
  env: any = null;
  altHeader: SafeHtml = null;
  doctorSignatureUrl: SafeUrl = null;

  labels = {
    he: {
      title: 'הפניה לבדיקות מעבדה', name: 'שם', id: 'ת.ז.', dob: 'תאריך לידה',
      hmo: 'קופת חולים', date: 'תאריך', code: 'קוד', instructions: 'הוראות',
      signedBy: 'נחתם ע"י', licenseNum: 'מספר רישיון', expertNum: 'מספר רישיון מומחה'
    },
    en: {
      title: 'Laboratory Test Referral', name: 'Name', id: 'ID', dob: 'Date of Birth',
      hmo: 'Health Provider', date: 'Date', code: 'Code', instructions: 'Instructions',
      signedBy: 'Signed by', licenseNum: 'License ', expertNum: 'Expert License '
    }
  };

  private hmoMap: { [key: string]: { he: string; en: string } } = {
    'Clalit': { he: 'כללית', en: 'Clalit' }, 'Macabi': { he: 'מכבי', en: 'Maccabi' },
    'Meuhedet': { he: 'מאוחדת', en: 'Meuhedet' }, 'Leumit': { he: 'לאומית', en: 'Leumit' },
    'Private': { he: 'פרטי', en: 'Private' }, 'IDF': { he: 'צבאי', en: 'IDF' },
  };

  private titleMap: { [key: string]: string } = {
    'מר': 'Mr.', 'גברת': 'Ms.', 'ד"ר': 'Dr.', 'פרופסור': 'Prof.', 'פרופ\'': 'Prof.', 'פרופ': 'Prof.',
    'Dr.': 'Dr.', 'Prof.': 'Prof.', 'Mr.': 'Mr.', 'Ms.': 'Ms.', 'Mrs.': 'Mrs.',
  };

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private signatureService: SignatureService
  ) {}

  ngOnInit() {
    const id = this.route.snapshot.params['id'];
    const langParam = this.route.snapshot.params['lang'];
    const code = this.route.snapshot.params['code'];
    this.lang = langParam === 'he-IL' ? 'he' : (langParam || 'he');
    this.dir = this.lang === 'he' ? 'rtl' : 'ltr';
    const now = new Date();
    const locale = this.lang === 'he' ? 'he-IL' : 'en-US';
    this.printDate = now.toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

    const loadData = () => {
      this.http.get<any>('/api/getenv').subscribe(
        envData => {
          this.env = envData;
          if (envData['htmlheader']) { this.altHeader = this.sanitizer.bypassSecurityTrustHtml(envData['htmlheader']); }
        },
        () => {}
      );
      this.http.get<any>(`/api/referrals/single/${id}`).subscribe(
        res => {
          this.referral = res;
          this.groups = this.groupItems(res.items || []);
          this.loaded = true;
          if (res.SignedAt && res.UID) { this.loadDoctorSignature(res.UID.toString()); }
        },
        () => { this.loaded = true; }
      );
    };

    if (code) {
      this.http.post('/api/referrals/resurection', { code }).subscribe(() => loadData(), () => loadData());
    } else {
      loadData();
    }
  }

  private groupItems(items: any[]): PrintGroup[] {
    const map: { [k: string]: PrintGroup } = {};
    const order: string[] = [];
    items.forEach(it => {
      const cat = it.Category || '';
      if (!map[cat]) { map[cat] = { name: cat, tests: [] }; order.push(cat); }
      map[cat].tests.push({ name: it.TestName || '', code: it.TestCode || '' });
    });
    return order.map(c => map[c]);
  }

  private loadDoctorSignature(uid: string) {
    this.signatureService.checkSignatureExists(uid).subscribe(
      (blob: Blob) => {
        if (blob && blob.size > 0) {
          this.doctorSignatureUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
        }
      },
      () => {}
    );
  }

  getSignerDisplay(referral: any): string {
    if (!referral) return '';
    const isEn = this.lang === 'en';
    const name = isEn && referral.OpenerEngName ? referral.OpenerEngName : (referral.Opener || '');
    const rawTitle = referral.OpenerTitle || '';
    const title = isEn ? (this.titleMap[rawTitle] || rawTitle) : rawTitle;
    return title ? title + ' ' + name : name;
  }

  getHMO(hmo: string): string {
    if (!hmo) return '';
    const entry = this.hmoMap[hmo];
    if (!entry) return hmo;
    return this.lang === 'en' ? entry.en : entry.he;
  }

  get lbl() { return this.labels[this.lang] || this.labels['he']; }

  formatDate(d: string): string {
    if (!d) return '';
    const locale = this.lang === 'he' ? 'he-IL' : 'en-US';
    return new Date(d).toLocaleDateString(locale);
  }
}
